<template>
  <main class="jobs-page">
    <section class="jobs-shell">
      <header class="jobs-header">
        <div>
          <p class="jobs-eyebrow">Scheduling</p>
          <h1>Jobs</h1>
        </div>

        <div class="header-actions">
          <div class="view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              class="view-toggle-button"
              :class="{ active: viewMode === 'calendar' }"
              @click="viewMode = 'calendar'"
            >
              <v-icon size="17">mdi-calendar-month-outline</v-icon>
              <span>Calendar</span>
            </button>
            <button
              type="button"
              class="view-toggle-button"
              :class="{ active: viewMode === 'list' }"
              @click="viewMode = 'list'"
            >
              <v-icon size="17">mdi-view-list-outline</v-icon>
              <span>List</span>
            </button>
          </div>

          <button class="new-job-button" type="button" @click="openCreate">
            <v-icon size="18">mdi-calendar-plus</v-icon>
            <span>New Job</span>
          </button>
        </div>
      </header>

      <div v-if="loadError" class="banner banner-error">{{ loadError }}</div>
      <div v-if="pageError" class="banner banner-error">{{ pageError }}</div>

      <template v-if="viewMode === 'calendar'">
        <div class="calendar-toolbar">
          <button class="month-nav-button" type="button" title="Previous month" @click="prevMonth">
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>
          <h2 class="month-label">{{ monthLabel }}</h2>
          <button class="month-nav-button" type="button" title="Next month" @click="nextMonth">
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>
          <button class="today-button" type="button" @click="goToday">Today</button>
        </div>

        <div class="calendar-panel">
          <div v-if="loading" class="empty-list">
            <v-progress-circular indeterminate size="24" color="primary" />
            <span>Loading jobs…</span>
          </div>

          <template v-else>
            <div class="weekday-row">
              <span v-for="day in weekdayLabels" :key="day">{{ day }}</span>
            </div>

            <div class="calendar-grid">
              <div
                v-for="cell in calendarCells"
                :key="cell.key"
                class="calendar-cell"
                :class="{ outside: cell.outside, today: cell.isToday }"
              >
                <span class="cell-day">{{ cell.dayNumber }}</span>

                <div class="cell-jobs">
                  <button
                    v-for="job in (jobsByDay[cell.key] || []).slice(0, 3)"
                    :key="job.id"
                    class="job-chip"
                    :class="{ cancelled: job.status === 'cancelled' }"
                    type="button"
                    :style="chipStyle(job)"
                    :title="job.title"
                    @click="openEdit(job)"
                  >
                    <span class="chip-time">{{ chipTime(job) }}</span>
                    <span class="chip-title">{{ job.title }}</span>
                  </button>

                  <button
                    v-if="(jobsByDay[cell.key] || []).length > 3"
                    class="more-link"
                    type="button"
                    @click="showDayInList(cell)"
                  >
                    +{{ (jobsByDay[cell.key] || []).length - 3 }} more
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </template>

      <template v-else>
        <div class="toolbar">
          <label class="search-field">
            <v-icon size="18">mdi-magnify</v-icon>
            <input v-model="searchTerm" type="text" placeholder="Search title, contact, address, quote #" />
          </label>

          <select v-model="statusFilter" class="status-filter">
            <option value="all">All statuses</option>
            <option v-for="status in jobStatuses" :key="status" :value="status">
              {{ jobStatusLabels[status] }}
            </option>
          </select>

          <button v-if="dayFilter" class="day-filter-chip" type="button" title="Clear day filter" @click="dayFilter = null">
            <span>{{ dayFilterLabel }}</span>
            <v-icon size="15">mdi-close</v-icon>
          </button>
        </div>

        <div class="table-panel">
          <div v-if="loading" class="empty-list">
            <v-progress-circular indeterminate size="24" color="primary" />
            <span>Loading jobs…</span>
          </div>

          <table v-else-if="filteredJobs.length" class="jobs-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Job</th>
                <th>Contact</th>
                <th>Quote</th>
                <th>Address</th>
                <th>Status</th>
                <th class="actions-col"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in filteredJobs" :key="job.id" class="job-row" @click="openEdit(job)">
                <td class="when-cell">{{ jobWhen(job) }}</td>
                <td><strong>{{ job.title }}</strong></td>
                <td>
                  <RouterLink
                    v-if="job.contact_id"
                    class="contact-link"
                    :to="`/crm/customers/${job.contact_id}`"
                    @click.stop
                  >
                    {{ job.contact_name || 'View contact' }}
                  </RouterLink>
                  <span v-else>—</span>
                </td>
                <td>{{ job.quote_number || '—' }}</td>
                <td>{{ job.address || '—' }}</td>
                <td @click.stop>
                  <select
                    class="row-status-select"
                    :value="job.status"
                    :style="chipStyle(job)"
                    @change="changeJobStatus(job, $event.target.value)"
                  >
                    <option v-for="status in jobStatuses" :key="status" :value="status">
                      {{ jobStatusLabels[status] }}
                    </option>
                  </select>
                </td>
                <td class="actions-col">
                  <button class="row-icon-button" type="button" title="Edit job" @click.stop="openEdit(job)">
                    <v-icon size="17">mdi-pencil-outline</v-icon>
                  </button>
                  <button class="row-icon-button danger" type="button" title="Delete job" @click.stop="deleteJob(job)">
                    <v-icon size="17">mdi-trash-can-outline</v-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-else class="empty-list">
            <v-icon size="34">mdi-briefcase-outline</v-icon>
            <h2>No jobs found</h2>
            <p>Schedule your first job or adjust the filters.</p>
          </div>
        </div>
      </template>
    </section>

    <JobFormModal
      v-model="showJobForm"
      :mode="jobFormMode"
      :job="activeJob"
      :company-id="company.company_id"
      @saved="onJobSaved"
    />
  </main>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'
import { company, getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'
import JobFormModal from '@/components/jobs/JobFormModal.vue'
import { jobStatuses, jobStatusLabels, jobStatusColors } from '@/constants/jobs'

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const jobs = ref([])
const loading = ref(false)
const loadError = ref('')
const pageError = ref('')

const viewMode = ref('calendar')
const now = new Date()
const currentMonth = ref(new Date(now.getFullYear(), now.getMonth(), 1))

const searchTerm = ref('')
const statusFilter = ref('all')
const dayFilter = ref(null)

const showJobForm = ref(false)
const jobFormMode = ref('create')
const activeJob = ref(null)

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function fetchJobs() {
  loading.value = true
  loadError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/jobs?company_id=${encodeURIComponent(company.company_id)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to load jobs.')
    }

    jobs.value = data.jobs || []
  } catch (err) {
    loadError.value = err.message || 'Failed to load jobs.'
  } finally {
    loading.value = false
  }
}

watchEffect(() => {
  if (company.company_id) fetchJobs()
})

function dayKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

const jobsByDay = computed(() => {
  const map = {}

  for (const job of jobs.value) {
    const start = new Date(job.scheduled_start)
    if (Number.isNaN(start.getTime())) continue

    const key = dayKey(start)
    if (!map[key]) map[key] = []
    map[key].push(job)
  }

  return map
})

const monthLabel = computed(() =>
  currentMonth.value.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
)

const calendarCells = computed(() => {
  const first = currentMonth.value
  const year = first.getFullYear()
  const month = first.getMonth()

  const startOffset = first.getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7

  const today = new Date()
  const todayKey = dayKey(today)

  const cells = []
  for (let i = 0; i < totalCells; i += 1) {
    const date = new Date(year, month, i - startOffset + 1)
    const key = dayKey(date)

    cells.push({
      key,
      date,
      dayNumber: date.getDate(),
      outside: date.getMonth() !== month,
      isToday: key === todayKey,
    })
  }

  return cells
})

function prevMonth() {
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() - 1, 1)
}

function nextMonth() {
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 1)
}

function goToday() {
  const today = new Date()
  currentMonth.value = new Date(today.getFullYear(), today.getMonth(), 1)
}

function chipTime(job) {
  return new Date(job.scheduled_start).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function chipStyle(job) {
  const palette = jobStatusColors[job.status] || jobStatusColors.scheduled
  return { color: palette.color, background: palette.background }
}

function jobWhen(job) {
  const start = new Date(job.scheduled_start)
  const startLabel = start.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  if (!job.scheduled_end) return startLabel

  const end = new Date(job.scheduled_end)
  const sameDay = dayKey(start) === dayKey(end)
  const endLabel = sameDay
    ? end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : end.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

  return `${startLabel} – ${endLabel}`
}

const dayFilterLabel = computed(() => {
  if (!dayFilter.value) return ''
  return dayFilter.value.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
})

const filteredJobs = computed(() => {
  const term = searchTerm.value.trim().toLowerCase()

  return jobs.value.filter((job) => {
    if (statusFilter.value !== 'all' && job.status !== statusFilter.value) return false
    if (dayFilter.value && dayKey(new Date(job.scheduled_start)) !== dayFilter.value.key) return false
    if (!term) return true

    return [job.title, job.contact_name, job.address, job.quote_number, job.description]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(term))
  })
})

function showDayInList(cell) {
  dayFilter.value = { key: cell.key, date: cell.date }
  viewMode.value = 'list'
}

function sortJobs() {
  jobs.value.sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start))
}

function openCreate() {
  jobFormMode.value = 'create'
  activeJob.value = null
  showJobForm.value = true
}

function openEdit(job) {
  jobFormMode.value = 'edit'
  activeJob.value = job
  showJobForm.value = true
}

function onJobSaved(job) {
  if (jobFormMode.value === 'create') {
    jobs.value.push(job)
  } else {
    const row = jobs.value.find((item) => item.id === job.id)
    if (row) Object.assign(row, job)
  }

  sortJobs()
}

async function changeJobStatus(job, status) {
  if (job.status === status) return

  pageError.value = ''
  const previous = job.status
  job.status = status

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/jobs/${job.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ status }),
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to update job status.')
    }

    Object.assign(job, data.job)

    const token = await getAuthToken()
    logAction(token, {
      action: 'job.status-change',
      label: `Marked job ${job.title} as ${jobStatusLabels[status] || status}`,
      company_id: company.company_id,
      metadata: { job_id: job.id, status },
    })
  } catch (err) {
    job.status = previous
    pageError.value = err.message || 'Failed to update job status.'
  }
}

async function deleteJob(job) {
  if (!confirm(`Delete job "${job.title}"? This can't be undone.`)) return

  pageError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/jobs/${job.id}`, {
      method: 'DELETE',
      headers,
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to delete job.')
    }

    jobs.value = jobs.value.filter((item) => item.id !== job.id)

    const token = await getAuthToken()
    logAction(token, {
      action: 'job.delete',
      label: `Deleted job ${job.title}`,
      company_id: company.company_id,
      metadata: { job_id: job.id },
    })
  } catch (err) {
    pageError.value = err.message || 'Failed to delete job.'
  }
}
</script>

<style scoped>
.jobs-page {
  min-height: calc(100dvh - 70px);
  padding: 28px 18px 104px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.jobs-shell {
  width: min(1440px, 100%);
  margin: 0 auto;
}

.jobs-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-end;
  margin-bottom: 18px;
}

.jobs-eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.jobs-header h1 {
  margin: 0;
  font-size: 2rem;
  line-height: 1.1;
  font-weight: 850;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.view-toggle {
  display: inline-flex;
  padding: 3px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
}

.view-toggle-button {
  height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 11px;
  border: 0;
  border-radius: 6px;
  color: var(--text-secondary);
  background: transparent;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;
}

.view-toggle-button.active {
  color: #1f5c4d;
  background: #e6f4ef;
}

.new-job-button {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #1f7a63;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
}

.banner {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.calendar-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.month-label {
  min-width: 180px;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 850;
  text-align: center;
}

.month-nav-button {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
}

.month-nav-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.today-button {
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;
}

.today-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.calendar-panel {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(23, 35, 31, 0.06);
  overflow: hidden;
}

.weekday-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid var(--border-subtle);
}

.weekday-row span {
  padding: 10px 12px;
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.calendar-cell {
  min-height: 112px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border-right: 1px solid var(--border-subtle);
  border-bottom: 1px solid var(--border-subtle);
}

.calendar-cell:nth-child(7n) {
  border-right: 0;
}

.calendar-cell.outside {
  background: var(--surface-alt);
}

.calendar-cell.outside .cell-day {
  color: var(--text-secondary);
}

.calendar-cell.today .cell-day {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #ffffff;
  background: #1f7a63;
}

.cell-day {
  color: var(--text-primary);
  font-size: 0.82rem;
  font-weight: 750;
}

.cell-jobs {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.job-chip {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
  padding: 3px 7px;
  border: 0;
  border-radius: 6px;
  font: inherit;
  font-size: 0.74rem;
  text-align: left;
  cursor: pointer;
}

.job-chip.cancelled {
  opacity: 0.55;
  text-decoration: line-through;
}

.chip-time {
  flex: 0 0 auto;
  font-weight: 800;
}

.chip-title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 650;
}

.more-link {
  align-self: flex-start;
  padding: 1px 2px;
  border: 0;
  color: #1f7a63;
  background: transparent;
  font: inherit;
  font-size: 0.74rem;
  font-weight: 750;
  cursor: pointer;
}

.more-link:hover {
  text-decoration: underline;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.search-field {
  height: 44px;
  flex: 1 1 320px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
}

.search-field input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  color: var(--text-primary);
  background: transparent;
  font-size: 0.92rem;
}

.status-filter {
  height: 44px;
  flex: 0 0 auto;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-primary);
  background: var(--surface);
  font: inherit;
  font-size: 0.88rem;
}

.day-filter-chip {
  height: 34px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 11px;
  border: 0;
  border-radius: 999px;
  color: #1f5c4d;
  background: #e6f4ef;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;
}

.table-panel {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(23, 35, 31, 0.06);
  overflow: auto;
}

.jobs-table {
  width: 100%;
  border-collapse: collapse;
}

.jobs-table th {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-subtle);
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
  text-align: left;
  white-space: nowrap;
}

.jobs-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-subtle);
  color: var(--text-primary);
  font-size: 0.88rem;
  vertical-align: middle;
}

.job-row {
  cursor: pointer;
}

.job-row:hover {
  background: var(--surface-alt);
}

.job-row:last-child td {
  border-bottom: 0;
}

.when-cell {
  white-space: nowrap;
}

.contact-link {
  color: #1f7a63;
  font-weight: 750;
  text-decoration: none;
}

.contact-link:hover {
  text-decoration: underline;
}

.row-status-select {
  height: 30px;
  padding: 0 8px;
  border: 0;
  border-radius: 999px;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 750;
  cursor: pointer;
}

.actions-col {
  width: 1%;
  white-space: nowrap;
  text-align: right;
}

.row-icon-button {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  margin-left: 6px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
}

.row-icon-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.row-icon-button.danger:hover {
  color: #8a2b1f;
  border-color: #f3c6bf;
  background: #fdecea;
}

.empty-list {
  min-height: 220px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  padding: 24px;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.empty-list h2 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.05rem;
}

.empty-list p {
  margin: 0;
}

@media (max-width: 900px) {
  .jobs-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .toolbar {
    flex-wrap: wrap;
  }

  .calendar-cell {
    min-height: 84px;
  }

  .chip-title {
    display: none;
  }
}
</style>
